import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import {
  CreditCard,
  Delete,
  Add,
  CheckCircle,
  ArrowBack,
  Edit,
  Lock,
  Security,
  VerifiedUser
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import axios from '../utils/axios';

// Load Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51O7EdZJOT9Vi6x');

const PaymentForm = () => {
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  const [cardError, setCardError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPaymentInfo();
    initializeStripe();
  }, []);

  const initializeStripe = async () => {
    try {
      const stripeInstance = await stripePromise;
      if (!stripeInstance) {
        console.error('Failed to load Stripe');
        return;
      }
      
      setStripe(stripeInstance);
      
      const elementsInstance = stripeInstance.elements();
      setElements(elementsInstance);
      
      const cardElementInstance = elementsInstance.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
              color: '#aab7c4',
            },
            ':-webkit-autofill': {
              color: '#fce883',
            },
          },
          invalid: {
            color: '#9e2146',
          },
        },
        hidePostalCode: true,
      });
      
      setCardElement(cardElementInstance);
      
      // Listen for card validation errors
      cardElementInstance.on('change', (event) => {
        setCardError(event.error ? event.error.message : null);
      });
    } catch (error) {
      console.error('Error initializing Stripe:', error);
      setError('Failed to load payment system. Please refresh the page.');
    }
  };

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get('/api/payment-info');
      setPaymentInfo(response.data);
    } catch (error) {
      console.error('Error fetching payment info:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!stripe || !elements || !cardElement) {
      setError('Payment system is not loaded. Please refresh the page.');
      setLoading(false);
      return;
    }

    if (!cardholderName.trim()) {
      setError('Please enter the cardholder name.');
      setLoading(false);
      return;
    }

    try {
      // Create payment method using Stripe Elements
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardholderName.trim(),
        },
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message);
        setLoading(false);
        return;
      }

      // Send payment method ID to backend
      const response = await axios.post('/api/payment-info', {
        paymentMethodId: paymentMethod.id,
        cardholderName: cardholderName.trim()
      });

      setSuccess(isEditMode ? 'Payment method updated successfully!' : 'Payment method added successfully! You can now book gyms.');
      setPaymentInfo(response.data);
      setShowForm(false);
      setIsEditMode(false);
      resetForm();
      
      // Refresh payment info
      await fetchPaymentInfo();
    } catch (error) {
      console.error('Error adding payment method:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.status === 400) {
        setError('Invalid payment information. Please check your card details.');
      } else {
        setError('Failed to add payment method. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePayment = async () => {
    if (!window.confirm('Are you sure you want to remove your payment method?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.delete('/api/payment-info');
      setPaymentInfo(null);
      setSuccess('Payment method removed successfully!');
    } catch (error) {
      console.error('Error removing payment method:', error);
      setError('Failed to remove payment method');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPayment = () => {
    if (!window.confirm('Are you sure you want to edit your payment method? Your current card will be replaced with the new details.')) {
      return;
    }
    setIsEditMode(true);
    setShowForm(true);
    setError(null);
    setSuccess(null);
    resetForm();
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setShowForm(false);
    resetForm();
    setError(null);
    setCardError(null);
  };

  const resetForm = () => {
    setCardholderName('');
    setCardError(null);
    if (cardElement) {
      cardElement.clear();
    }
  };

  const getCardBrandIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      case 'amex':
        return '💳';
      case 'discover':
        return '💳';
      default:
        return <CreditCard />;
    }
  };

  const getCardBrandName = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return 'Visa';
      case 'mastercard':
        return 'Mastercard';
      case 'amex':
        return 'American Express';
      case 'discover':
        return 'Discover';
      default:
        return brand || 'Card';
    }
  };

  // Mount card element when form is shown
  useEffect(() => {
    if (showForm && cardElement) {
      const cardContainer = document.getElementById('card-element');
      if (cardContainer) {
        cardElement.mount('#card-element');
      }
    }
  }, [showForm, cardElement]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>{success}</Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button variant="outlined" size="small" onClick={() => navigate(-1)}>
              Go Back
            </Button>
            <Button variant="contained" size="small" onClick={() => navigate('/gyms')}>
              Browse Gyms
            </Button>
          </Box>
        </Alert>
      )}

      {paymentInfo?.hasPaymentMethod && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Current Payment Method</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Edit Payment Method">
                <IconButton color="primary" onClick={handleEditPayment} disabled={loading}>
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove Payment Method">
                <IconButton color="error" onClick={handleRemovePayment} disabled={loading}>
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ fontSize: '2rem' }}>
                  {getCardBrandIcon(paymentInfo.cardBrand)}
                </Box>
                <Box>
                  <Typography variant="h6">
                    {getCardBrandName(paymentInfo.cardBrand)} •••• {paymentInfo.lastFourDigits}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expires {paymentInfo.expiryMonth}/{paymentInfo.expiryYear}
                  </Typography>
                </Box>
                <Chip 
                  icon={<CheckCircle />} 
                  label="Active" 
                  color="success" 
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              </Box>
            </CardContent>
          </Card>
        </Paper>
      )}

      {!paymentInfo?.hasPaymentMethod && !showForm && (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>No Payment Method Added</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You need to add a payment method to book gyms.
          </Typography>
          <Alert 
            severity="info" 
            icon={<Security />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" component="div">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Lock sx={{ fontSize: 18 }} />
                <strong>Secure Payment Processing</strong>
              </Box>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                <li>Your card information is processed securely by <strong>Stripe</strong>, a trusted payment processor used by millions of businesses worldwide</li>
                <li>We never see or store your full card details on our servers</li>
                <li>Your card data is encrypted and transmitted directly to Stripe using industry-standard SSL/TLS encryption</li>
                <li>Stripe is PCI DSS Level 1 certified (the highest level of security certification for payment processors)</li>
              </Box>
              <Typography variant="body2" sx={{ mt: 1, fontSize: '0.85rem' }}>
                Learn more about Stripe security: <a href="https://stripe.com/docs/security" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>stripe.com/security</a>
              </Typography>
            </Typography>
          </Alert>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => setShowForm(true)} 
            startIcon={<Add />}
          >
            Add Payment Method
          </Button>
        </Paper>
      )}

      {showForm && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {isEditMode ? 'Edit Payment Method' : 'Add Payment Method'}
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" component="div">
              <strong>🔒 Secure Payment Processing by Stripe</strong>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Your card information is encrypted and sent directly to Stripe. We never store your full card details on our servers. Stripe is PCI DSS Level 1 certified.
                {isEditMode && (
                  <span> For security reasons, please re-enter your card details. Your current card will be replaced.</span>
                )}
                {' '}
                <a href="https://stripe.com/docs/security" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Learn more about Stripe security</a>
              </Typography>
            </Typography>
          </Alert>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Cardholder Name"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
              placeholder="John Doe"
              required
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Lock sx={{ fontSize: 18, color: 'success.main' }} />
                <Typography variant="body2" color="text.secondary">
                  Card Details (Secured by Stripe)
                </Typography>
              </Box>
              <Box 
                id="card-element" 
                sx={{ 
                  border: cardError ? '1px solid #d32f2f' : '1px solid #ccc', 
                  borderRadius: 1, 
                  p: 2, 
                  minHeight: '40px',
                  backgroundColor: '#fafafa',
                  position: 'relative',
                  '& .StripeElement': {
                    padding: '8px'
                  },
                  '&:focus-within': {
                    borderColor: 'primary.main',
                    borderWidth: '2px'
                  }
                }}
              />
              {cardError && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {cardError}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Security sx={{ fontSize: 14 }} />
                Your card details are encrypted and secured
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading || !!cardError}
                startIcon={<Lock />}
              >
                {loading ? 'Processing...' : isEditMode ? 'Update Payment Method' : 'Add Payment Method'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleCancelEdit} 
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Box>
          
          {/* Security Trust Indicators */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, textAlign: 'center' }}>
              Secured by industry-leading security standards
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VerifiedUser sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary">
                  PCI DSS Level 1
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Security sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary">
                  SSL Encrypted
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Lock sx={{ fontSize: 20, color: 'info.main' }} />
                <Typography variant="caption" color="text.secondary">
                  Powered by Stripe
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box component="img" 
                src="https://stripe.com/img/v3/home/social.png" 
                alt="Stripe" 
                sx={{ height: 24, opacity: 0.6, filter: 'grayscale(100%)' }}
                onError={(e) => {
                  // Fallback if image doesn't load
                  e.target.style.display = 'none';
                }}
              />
              <Typography 
                variant="caption" 
                component="a" 
                href="https://stripe.com" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Powered by Stripe
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default PaymentForm; 